import { cn } from '@/lib/utils';

interface GarminAttributionProps {
  className?: string;
  /** Device model name from Garmin webhook (e.g. "Forerunner 265"). */
  deviceModel?: string | null;
}

/**
 * Required attribution when displaying Garmin-sourced activity data.
 * Per Garmin API Brand Guidelines: show "Garmin [device model]" when available,
 * otherwise fall back to "Garmin Connect™".
 */
export function GarminAttribution({ className, deviceModel }: GarminAttributionProps) {
  const model = deviceModel?.replace(/^Garmin\s+/i, '') ?? null;
  const source = model ? `Garmin ${model}` : 'Garmin Connect\u2122';
  const text = `Activity data provided by ${source}`;

  return (
    <p
      className={cn('text-[10px] text-muted-foreground leading-tight', className)}
      role="note"
      aria-label={text}
    >
      {text}
    </p>
  );
}

/**
 * Attribution for derived/analytics views (PMC, coaching insights, AI analysis).
 * Per Garmin API Brand Guidelines: required on any screen showing data derived
 * from Garmin device metrics.
 */
export function GarminDerivedAttribution({ className }: { className?: string }) {
  return (
    <p
      className={cn('text-[9px] text-muted-foreground/70 leading-tight', className)}
      role="note"
    >
      Insights derived in part from Garmin device-sourced data
    </p>
  );
}
